import {Button, Stack} from '@mui/material';
import {type OptimizationRun, OptimizationRunStateEnum} from 'client';
import {dequal} from 'dequal/lite';
import Alert from 'helpers/Alert';
import {
  EMPTY_ID,
  useGetPlanActiveStatusPolling,
  useGetPlanOptimizationState,
  useGetPlanOptimizationStatePolling,
} from 'hooks/swr';
import useIsMounted from 'hooks/useIsMounted';
import useParseURL from 'hooks/useParseURL';
import {SWRKeyEnum, URLParams} from 'interfaces';
import {type SnackbarAction, useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import backendCommunicator from 'services/backendCommunicator';
import {mutate, unstable_serialize, useSWRConfig} from 'swr';
import {navigateToOptimizationRun} from '../components/OptimizationRuns/AllRunsTable';

import * as Sentry from '@sentry/react';

const IN_PROGRESS_STATES = [OptimizationRunStateEnum.Started, OptimizationRunStateEnum.Pending];

export const usePlanOptimization = ({dontEnqueueSnackbar}: {dontEnqueueSnackbar: boolean}) => {
  const {cache} = useSWRConfig();
  const {t} = useTranslation('common');
  const isMounted = useIsMounted();
  const {navigate, planId, pathname, urlSearchParams} = useParseURL();

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [isHappyEnd, setIsHappyEnd] = useState(false); // user witnesses optimization success
  const [optimizationRun, setOptimizationRun] = useState<undefined | OptimizationRun>();
  const [isSnackBar, setIsSnackBar] = useState<string | number>();

  const {data: planStatus} = useGetPlanActiveStatusPolling({
    resourceId: (!optimizationRun || !isInProgressState(optimizationRun.state)) && !isHappyEnd ? planId : undefined,
  });

  const {data: optimizationStatusOnLoad} = useGetPlanOptimizationState({
    resourceId: !optimizationRun ? planId : undefined,
  });

  const {data: optimizationStatus} = useGetPlanOptimizationStatePolling({
    resourceId: optimizationRun && isInProgressState(optimizationRun.state) ? planId : undefined,
  });

  const triggerPlanOptimization = async (planId: string) => {
    await Sentry.startSpan({name: 'Start Optimization'}, async () => {
      try {
        cache.delete(unstable_serialize([planId, SWRKeyEnum.GET_PLAN_OPTIMIZATION_STATE_POLLING]));
        const response = await backendCommunicator.runOptimization({planId});
        setOptimizationRun(response.data);
        mutate([EMPTY_ID, SWRKeyEnum.GET_OPTIMIZATION_RUNS]);
      } catch (error) {
        isMounted.current && Alert.error(error);
      }
    });
  };

  const navigateToNewPlan = (newPlanId: string) => {
    urlSearchParams.delete(URLParams.site);
    urlSearchParams.delete(URLParams.cage);
    urlSearchParams.set(URLParams.plan, newPlanId);
    navigate({
      pathname,
      search: urlSearchParams.toString(),
    });
  };

  useEffect(() => {
    setIsHappyEnd(false);
    setOptimizationRun(undefined);
    closeSnackbar();
  }, [planId]);

  // Checking optimization status on initial app load
  useEffect(() => {
    if (
      optimizationStatusOnLoad &&
      optimizationStatusOnLoad.length > 0 &&
      isInProgressState(optimizationStatusOnLoad[0].state)
    ) {
      setOptimizationRun(optimizationStatusOnLoad[0]);
    }
  }, [optimizationStatusOnLoad]);

  useEffect(() => {
    if (optimizationStatus && optimizationStatus.length > 0 && !dequal(optimizationRun, optimizationStatus[0])) {
      const currentRun = optimizationStatus[0];
      if (
        optimizationRun &&
        optimizationRun.state !== OptimizationRunStateEnum.Success &&
        currentRun.state === OptimizationRunStateEnum.Success &&
        currentRun.optimizedPlanId !== undefined &&
        !dontEnqueueSnackbar &&
        !isSnackBar
      ) {
        const newPlanId = currentRun.optimizedPlanId;
        setIsHappyEnd(true);
        void mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
        void mutate([planId, SWRKeyEnum.GET_PLAN]);

        setIsSnackBar(
          enqueueSnackbar(t('planOptimized'), {
            persist: true,
            action: planStatusSnackbarActionFactory({
              onClick: () => {
                closeSnackbar();
                navigateToNewPlan(newPlanId);
                setIsSnackBar(undefined);
              },
              text: t('gotoNewVersion'),
              closeSnackbar,
              dismissText: t('dismiss'),
            }),
            variant: 'success',
          }),
        );
      } else if (currentRun.state === OptimizationRunStateEnum.Failure) {
        setIsSnackBar(
          enqueueSnackbar(t('optimizationFailed'), {
            persist: true,
            action: planStatusSnackbarActionFactory({
              onClick: () => {
                closeSnackbar();
                navigateToOptimizationRun(currentRun.id, navigate, urlSearchParams);
                setIsSnackBar(undefined);
              },
              text: t('gotoOptimizationRun'),
              closeSnackbar,
              dismissText: t('dismiss'),
            }),
            variant: 'error',
          }),
        );
      }
      setOptimizationRun(currentRun);
    }
  }, [optimizationStatus]);

  useEffect(() => {
    if (
      planStatus?.active === false &&
      planStatus.newId &&
      optimizationStatus === undefined &&
      !dontEnqueueSnackbar &&
      !isSnackBar
    ) {
      void mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
      void mutate([planId, SWRKeyEnum.GET_PLAN]);
      const newPlanId = planStatus.newId;
      setIsSnackBar(
        enqueueSnackbar(t('planUpdated'), {
          persist: true,
          action: planStatusSnackbarActionFactory({
            onClick: () => {
              closeSnackbar();
              navigateToNewPlan(newPlanId);
              setIsSnackBar(undefined);
            },
            text: t('gotoNewVersion'),
            closeSnackbar,
            dismissText: t('dismiss'),
          }),
          variant: 'info',
        }),
      );
    }
  }, [planStatus]);

  return {
    triggerPlanOptimization,
    optimizationRun,
  };
};

export const isInProgressState = (optimizationState: OptimizationRunStateEnum) => {
  return IN_PROGRESS_STATES.some((state) => state === optimizationState);
};

export const planStatusSnackbarActionFactory = ({
  onClick,
  text,
  dismissText,
  closeSnackbar,
}: {
  onClick: () => void;
  text: string;
  dismissText: string;
  closeSnackbar: () => void;
}): SnackbarAction => {
  return () => (
    <Stack spacing={1} direction={'row'}>
      <Button onClick={onClick} variant="text" color="inherit" data-testid="go-to-optimized-plan-button">
        {text}
      </Button>
      <Button
        onClick={() => {
          closeSnackbar();
        }}
        variant="text"
        color="inherit"
      >
        {dismissText}
      </Button>
    </Stack>
  );
};
